import { useQuery, useApolloClient } from '@apollo/client'
import {
  BUSINESS_CLIENT_SUGGESTION_FRAGMENT,
  GET_BUSINESS_CLIENTS_SUGGESTIONS
} from 'features/BusinessClients/queries-legacy'
import { BusinessClientSuggestion, BusinessClientSuggestionsResponse } from 'gql/generated/graphql'

interface IUseBusinessClientAutocompleteOptions {
  skip?: boolean
}

export const useBusinessClientAutocomplete = (
  businessClientId: string,
  { skip }: IUseBusinessClientAutocompleteOptions = {}
): BusinessClientSuggestion | null => {
  const client = useApolloClient()

  // Check cache using fragment
  const cachedClient = client.readFragment<BusinessClientSuggestion>({
    id: `BusinessClientSuggestion:${businessClientId}`,
    fragment: BUSINESS_CLIENT_SUGGESTION_FRAGMENT
  })

  const skipQuery = skip || !!cachedClient

  const { data } = useQuery<{ businessClientSuggestions: BusinessClientSuggestionsResponse }>(
    GET_BUSINESS_CLIENTS_SUGGESTIONS,
    {
      variables: {
        input: {
          id: businessClientId
        }
      },
      skip: skipQuery
    }
  )

  return cachedClient || data?.businessClientSuggestions?.items.at(0) || null
}

interface IUseBusinessClientsAutocompleteOptions {
  skip?: boolean
}

export const useBusinessClientsAutocomplete = (
  businessClientIds: string[],
  { skip }: IUseBusinessClientsAutocompleteOptions = {}
): BusinessClientSuggestion[] => {
  const client = useApolloClient()

  // Check cache using fragments
  const cachedClients = businessClientIds.map((id) =>
    client.readFragment<BusinessClientSuggestion>({
      id: `BusinessClientSuggestion:${id}`,
      fragment: BUSINESS_CLIENT_SUGGESTION_FRAGMENT
    })
  )

  const allCached = cachedClients.every((client) => !!client)
  const skipQuery = skip || businessClientIds.length === 0 || allCached

  const { data } = useQuery<{ businessClientSuggestions: BusinessClientSuggestionsResponse }>(
    GET_BUSINESS_CLIENTS_SUGGESTIONS,
    {
      variables: {
        input: {
          ids: businessClientIds
        }
      },
      skip: skipQuery
    }
  )

  return allCached ? (cachedClients as BusinessClientSuggestion[]) : data?.businessClientSuggestions?.items || []
}
